import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, BarChart3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface HistoricalFXCardProps {
  baseCurrency?: string;
}

export function HistoricalFXCard({ baseCurrency = 'USD' }: HistoricalFXCardProps) {
  const [windowDays, setWindowDays] = useState(30);
  const [averaged, setAveraged] = useState<Record<string, number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [lastApplied, setLastApplied] = useState<string | null>(null);
  const { toast } = useToast();

  async function loadAveragedRates() {
    setLoading(true);
    try {
      // Try to get pre-computed averages
      const { data: precomputed, error: precomputedError } = await supabase
        .from('fx_rates_averaged')
        .select('rates, calculated_at, date_range_start, date_range_end')
        .eq('base_currency', baseCurrency)
        .eq('window_days', windowDays)
        .order('calculated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (precomputedError) throw precomputedError;

      if (precomputed?.rates) {
        setAveraged(precomputed.rates as Record<string, number>);
        setLastApplied(new Date(precomputed.calculated_at).toLocaleDateString());
      } else {
        // Calculate on-the-fly from recent rates
        const { data: recentRates, error: ratesError } = await supabase
          .from('fx_rates')
          .select('date, rates')
          .eq('base_currency', baseCurrency)
          .order('date', { ascending: false })
          .limit(windowDays);

        if (ratesError) throw ratesError;

        if (recentRates && recentRates.length > 0) {
          const avg: Record<string, number> = {};
          const allCurrencies = new Set<string>();

          // Collect all currencies
          recentRates.forEach((day: any) => {
            if (day.rates && typeof day.rates === 'object') {
              Object.keys(day.rates).forEach((currency) => allCurrencies.add(currency));
            }
          });

          // Calculate averages
          allCurrencies.forEach((currency) => {
            const values: number[] = [];
            recentRates.forEach((day: any) => {
              const rate = day.rates?.[currency];
              if (rate && typeof rate === 'number' && rate > 0) {
                values.push(rate);
              }
            });

            if (values.length > 0) {
              avg[currency] = values.reduce((a, v) => a + v, 0) / values.length;
            }
          });

          setAveraged(avg);
          setLastApplied('Calculated on-the-fly');
        } else {
          setAveraged(null);
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error loading averaged FX rates',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function applySmoothing() {
    setComputing(true);
    try {
      const { data, error } = await supabase.functions.invoke('compute-historical-averages', {
        body: { base: baseCurrency, windowDays },
      });

      if (error) throw error;

      toast({
        title: 'FX smoothing applied',
        description: `Successfully computed ${windowDays}-day rolling average using ${data.data_points_used} data points`,
      });

      await loadAveragedRates();
    } catch (error: any) {
      toast({
        title: 'Error applying FX smoothing',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setComputing(false);
    }
  }

  useEffect(() => {
    loadAveragedRates();
  }, [windowDays]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Historical FX Averaging
            </CardTitle>
            <CardDescription>
              Smooth exchange rates over configurable windows for consistent reporting
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(parseInt(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7-day avg</SelectItem>
                <SelectItem value="30">30-day avg</SelectItem>
                <SelectItem value="90">90-day avg</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={applySmoothing} disabled={computing}>
              {computing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Apply
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {lastApplied && (
          <p className="text-sm text-muted-foreground">
            <strong>Last calculated:</strong> {lastApplied}
          </p>
        )}

        {loading ? (
          <div className="p-4 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : averaged && Object.keys(averaged).length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {Object.entries(averaged)
              .filter(([currency]) => ['USD', 'ZAR', 'GBP', 'EUR', 'AED', 'KES'].includes(currency))
              .map(([currency, rate]) => (
                <div
                  key={currency}
                  className="flex justify-between items-center border rounded-md p-3 bg-card"
                >
                  <span className="font-medium">{currency}</span>
                  <span className="text-muted-foreground">{Number(rate).toFixed(3)}</span>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <p>No FX data yet — fetch rates first.</p>
            <p className="text-sm mt-1">
              Use the FX Normalization card above to fetch current rates.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
